import { useEffect, useState } from 'preact/hooks';
import { BASE_PATH, type EventRecord } from '../../shared/types';
import { api } from '../lib/api';
import { AppIcon } from './AppIcon';

interface Status { connected: boolean; submissions?: number; headerInSync?: boolean; revoked?: boolean; error?: string }

const MESSAGES: Record<string, string> = {
  connected: 'Google account connected. Your sheet and folder are ready.',
  denied: 'Google access was not granted. Nothing was changed.',
  scope: 'Google access was granted without permission to create files. Try again and keep the Drive box checked.',
  no_refresh: 'Google did not return a long-lived token. Try again and choose Allow on the consent screen.',
  error: 'Something went wrong connecting to Google. Please try again.',
};

/** `onReview` opens the Review and publish step; unset while that step can't be reached. */
export function GoogleConnect({ event, onChange, beforeConnect, onReview }: { event: EventRecord; onChange: (e: EventRecord) => void; beforeConnect?: () => Promise<boolean>; onReview?: () => void }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const flash = new URLSearchParams(location.search).get('google');

  useEffect(() => {
    if (event.google.connected) api.get<Status>(`/google/status/${event.id}`).then(setStatus).catch(() => setStatus({ connected: true, error: 'unreachable' }));
  }, [event.id, event.google.connected]);

  const disconnect = async () => {
    if (!confirm('Disconnect Google Drive? Registration will close. Your spreadsheet, signed forms, and uploaded files stay in your Google Drive.')) return;
    setBusy(true);
    try { onChange(await api.post<EventRecord>(`/google/disconnect/${event.id}`)); } finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true);
    try { if (beforeConnect && !await beforeConnect()) return; location.href = `${BASE_PATH}/api/google/start?event=${event.id}`; }
    finally { setBusy(false); }
  };
  const g = event.google;
  return (
    <section class="stack">
      <h2>Keep registrations in your Google Drive</h2>
      {flash && MESSAGES[flash] && <p class={flash === 'connected' ? 'notice' : 'alert'}>{MESSAGES[flash]}</p>}
      <p class="muted">{g.connected ? 'Registration answers and signed forms are saved in your event folder.' : 'Connect your Google account to create an event folder for registration answers, signed forms, and uploaded files.'}</p>
      <div class="google-output-cards"><div><AppIcon name="forms" /><h3>Registration spreadsheet</h3><p>Every registration, organized in one Google Sheet.</p></div><div><AppIcon name="forms" /><h3>Signed forms</h3><p>Each participant’s signed forms, saved as PDFs in your event folder.</p></div></div>

      {!g.connected ? (
        <div class="card stack">
          {g.spreadsheetId && <p class="muted small">Previously connected. Reconnecting with the same Google account reuses your existing sheet and folder.</p>}
          <button disabled={busy} onClick={() => { void connect(); }}>{busy ? 'Preparing your event…' : 'Connect Google account'}</button>
        </div>
      ) : (
        <div class="card stack google-connected">
          <div>Connected as <strong>{g.accountEmail}</strong></div>
          <ul>
            <li><a href={`https://docs.google.com/spreadsheets/d/${g.spreadsheetId}`} target="_blank" rel="noopener">Open registration spreadsheet</a></li>
            <li><a href={`https://drive.google.com/drive/folders/${g.folderId}`} target="_blank" rel="noopener">Open event folder</a></li>
          </ul>
          <p class="muted">Share the event folder with other organizers who need access to registrations and forms.</p>
          {status === null ? <p class="muted small">Checking connection…</p>
            : status.revoked ? <p class="alert">Google access has been revoked. Reconnect to resume registrations.</p>
            : status.error ? <p class="alert">Could not reach your sheet right now. If this persists, reconnect Google.</p>
            // The sheet's column headers catch up on their own with the next registration, so there's nothing to say about them.
            : <p class="muted small">{status.submissions} {status.submissions === 1 ? 'registration' : 'registrations'} received.</p>}
          {/* What disconnecting does, right above the link, so it's read before clicking; the confirmation repeats it. */}
          <p class="muted small">Disconnecting closes registration. Your spreadsheet, signed forms, and uploaded files stay in your Google Drive.</p>
          <div class="row">
            {status?.revoked && <button disabled={busy} onClick={() => { void connect(); }}>Reconnect Google</button>}
            <button class="link danger" disabled={busy} onClick={disconnect}>Disconnect Google Drive</button>
          </div>
          <p class="muted small">To reopen registration, reconnect with the same Google account to reuse this event’s folder, then publish again from {onReview ? <button type="button" class="link inline-link" onClick={onReview}>Review and publish</button> : 'Review and publish'}.</p>
        </div>
      )}
      <p class="google-privacy"><AppIcon name="key" /><span>Your files belong to you. We only request access to files this site creates; other files in your Drive remain private.</span></p>
    </section>
  );
}
